import {
  DEFAULT_FONT_SIZE,
  type RichTextHighlight,
  type RichTextRkpiSave,
  type RichTextTextAnimation,
} from "@library-elements/text";
import {
  type Mark,
  type MarkType,
  type Node as PmNode,
  type NodeType,
  type ResolvedPos,
} from "prosemirror-model";
import {
  liftListItem,
  sinkListItem,
  splitListItem,
  wrapInList,
} from "prosemirror-schema-list";
import {
  NodeSelection,
  Selection,
  TextSelection,
  type Command,
  type EditorState,
  type Transaction,
} from "prosemirror-state";
import {
  joinTextblockBackward,
  splitBlockAs,
  toggleMark,
} from "prosemirror-commands";
import { Mapping, type Mappable } from "prosemirror-transform";
import type { EditorView } from "prosemirror-view";
import {
  MAX_LIST_DEPTH,
  blockImpliedStyle,
  listDepthAt,
  maxListDepthIn,
} from "./normalize";
import {
  textSchema,
  type Alignment,
  type BlockAttrs,
  type Direction,
  type ListKind,
  type RkpiAttrs,
  rkpiText,
} from "./schema";
import {
  headingLevelToStyle,
  styleToHeadingLevel,
  type TextStyleId,
} from "./typography";

const { nodes, marks } = textSchema;

// ─── selection helpers ──────────────────────────────────────────────

/**
 * Spec §10 — the toolbar reflects the FIRST text segment of the selection;
 * for a caret that is the marks typing would produce.
 */
export const firstSegmentMarks = (state: EditorState): readonly Mark[] => {
  const { $from, from, to, empty } = state.selection;
  if (empty) return state.storedMarks || $from.marks();
  let found: readonly Mark[] | null = null;
  state.doc.nodesBetween(from, to, (node) => {
    if (found) return false;
    if (node.isInline && node.type !== nodes.hard_break) {
      found = node.marks;
      return false;
    }
    return true;
  });
  return found ?? $from.marks();
};

/** Froala parity: a caret applies element-wide; a range applies to the range. */
const targetRange = (state: EditorState): { from: number; to: number } => {
  const { from, to, empty } = state.selection;
  return empty ? { from: 0, to: state.doc.content.size } : { from, to };
};

/** Extent of `type` around a position (for editing the mark the caret is in). */
export const markExtentAt = (
  $pos: ResolvedPos,
  type: MarkType,
): { from: number; to: number; mark: Mark } | null => {
  const parent = $pos.parent;
  const index = $pos.index();
  const start = parent.maybeChild(index) ?? parent.maybeChild(index - 1);
  const mark = start ? type.isInSet(start.marks) : null;
  if (!mark) return null;
  let startIndex = $pos.index();
  let endIndex = $pos.indexAfter();
  // Widen while adjacent children carry the same mark.
  while (startIndex > 0 && mark.isInSet(parent.child(startIndex - 1).marks))
    startIndex--;
  while (
    endIndex < parent.childCount &&
    mark.isInSet(parent.child(endIndex).marks)
  )
    endIndex++;
  let from = $pos.start();
  for (let i = 0; i < startIndex; i++) from += parent.child(i).nodeSize;
  let to = from;
  for (let i = startIndex; i < endIndex; i++) to += parent.child(i).nodeSize;
  return { from, to, mark };
};

// ─── inline marks (spec §6, §10) ────────────────────────────────────

/**
 * Bold/italic/underline/strike: the decision comes from the first segment
 * and is applied to the whole selection, overriding mixed states. A caret
 * toggles the stored marks so typing continues in the new state (default
 * ProseMirror behaviour).
 */
export const setMarkFromFirstSegment =
  (type: MarkType): Command =>
  (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (empty) return toggleMark(type)(state, dispatch);
    const active = !!type.isInSet(firstSegmentMarks(state));
    if (dispatch) {
      const tr = state.tr.removeMark(from, to, type);
      if (!active) tr.addMark(from, to, type.create());
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

export const setColor =
  (color: string | null): Command =>
  (state, dispatch) => {
    const { from, to } = targetRange(state);
    if (dispatch) {
      const tr = state.tr.removeMark(from, to, marks.color);
      if (color) tr.addMark(from, to, marks.color.create({ color }));
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

export const setHighlight =
  (data: RichTextHighlight | null): Command =>
  (state, dispatch) => {
    const { $from, from, to, empty } = state.selection;
    const attrs = data
      ? {
          bgColor: data.bgColor,
          textColor: data.textColor,
          animation: data.animation,
          static: !!data.static,
        }
      : null;

    if (empty) {
      // Caret: edit/remove the highlight the caret sits in, else nothing.
      const extent = markExtentAt($from, marks.highlight);
      if (!extent) return false;
      if (dispatch) {
        const tr = state.tr.removeMark(extent.from, extent.to, marks.highlight);
        if (attrs)
          tr.addMark(extent.from, extent.to, marks.highlight.create(attrs));
        dispatch(tr);
      }
      return true;
    }

    if (dispatch) {
      const tr = state.tr.removeMark(from, to, marks.highlight);
      if (attrs) tr.addMark(from, to, marks.highlight.create(attrs));
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

const FORMATTING_MARKS = () => [
  marks.strong,
  marks.em,
  marks.underline,
  marks.strike,
  marks.color,
  marks.highlight,
  marks.text_style,
  marks.font_size,
  marks.subscript,
  marks.superscript,
];

/** Spec §9 — Cmd/Ctrl+\ : drop inline formatting, keep structure and links. */
export const clearFormatting: Command = (state, dispatch) => {
  const { from, to } = targetRange(state);
  if (dispatch) {
    const tr = state.tr;
    for (const type of FORMATTING_MARKS()) tr.removeMark(from, to, type);
    tr.setStoredMarks([]);
    dispatch(tr.scrollIntoView());
  }
  return true;
};

// ─── typography (spec §3) ───────────────────────────────────────────

type BlockHit = {
  node: PmNode;
  pos: number;
  listItem: { node: PmNode; pos: number } | null;
};

const textblocksIn = (doc: PmNode, from: number, to: number): BlockHit[] => {
  const hits: BlockHit[] = [];
  let currentItem: { node: PmNode; pos: number; end: number } | null = null;
  doc.nodesBetween(from, to, (node, pos) => {
    if (node.type === nodes.list_item) {
      currentItem = { node, pos, end: pos + node.nodeSize };
      return true;
    }
    if (node.isTextblock) {
      const item =
        currentItem && pos > currentItem.pos && pos < currentItem.end
          ? currentItem
          : null;
      hits.push({
        node,
        pos,
        listItem: item ? { node: item.node, pos: item.pos } : null,
      });
      return false;
    }
    return true;
  });
  return hits;
};

/** Block attributes that survive a re-tag (everything but the heading level). */
const blockAttrsOf = (node: PmNode): BlockAttrs => ({
  align: (node.attrs.align as BlockAttrs["align"]) ?? null,
  lineHeight: (node.attrs.lineHeight as BlockAttrs["lineHeight"]) ?? null,
  direction: (node.attrs.direction as BlockAttrs["direction"]) ?? null,
  animation: (node.attrs.animation as BlockAttrs["animation"]) ?? null,
});

const applyBlockStyle = (
  tr: Transaction,
  hit: BlockHit,
  style: TextStyleId,
) => {
  const { node, pos, listItem } = hit;
  // Positions stay valid: setNodeMarkup/removeMark never change node sizes.
  tr.removeMark(pos + 1, pos + 1 + node.content.size, marks.text_style);
  // A text style replaces any explicit size (Froala did the same).
  tr.removeMark(pos + 1, pos + 1 + node.content.size, marks.font_size);
  if (listItem) {
    // Items hold paragraphs; the style lives on the item (spec §3.7).
    tr.setNodeMarkup(listItem.pos, undefined, {
      ...listItem.node.attrs,
      textStyle: style === "normal" ? null : style,
    });
    if (node.type !== nodes.paragraph) {
      tr.setNodeMarkup(pos, nodes.paragraph, blockAttrsOf(node));
    }
    return;
  }
  const level = styleToHeadingLevel(style);
  tr.setNodeMarkup(
    pos,
    level ? nodes.heading : nodes.paragraph,
    level ? { level, ...blockAttrsOf(node) } : blockAttrsOf(node),
  );
};

/**
 * Spec §3.2 — a style applies at block level when the selection is a caret
 * (whole element, Froala parity) or covers whole blocks; a partial selection
 * gets an inline `text_style` segment instead (mixed styles in one element).
 */
export const setTextStyle =
  (style: TextStyleId): Command =>
  (state, dispatch) => {
    const { from, to, empty } = state.selection;
    const range = targetRange(state);
    const hits = textblocksIn(state.doc, range.from, range.to);
    if (hits.length === 0) return false;

    const coversWholeBlocks =
      empty ||
      hits.every(
        (h) => from <= h.pos + 1 && to >= h.pos + 1 + h.node.content.size,
      );

    if (dispatch) {
      const tr = state.tr;
      if (coversWholeBlocks) {
        for (const hit of hits) applyBlockStyle(tr, hit, style);
      } else {
        tr.removeMark(from, to, marks.text_style);
        // A segment styled like its block needs no wrapper.
        const hit = hits[0];
        const implied = blockImpliedStyle(hit.node, hit.listItem?.node ?? null);
        if (style !== implied || hits.length > 1) {
          tr.addMark(from, to, marks.text_style.create({ style }));
        }
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

// ─── alignment (spec §4.4 — markers follow via CSS) ─────────────────

export const setAlignment =
  (align: Alignment): Command =>
  (state, dispatch) => {
    const { from, to } = targetRange(state);
    if (dispatch) {
      const tr = state.tr;
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type === nodes.list_item) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, align });
          // Blocks inside inherit from the item; clear their own value.
          node.forEach((child, offset) => {
            if (child.isTextblock && child.attrs.align != null) {
              tr.setNodeMarkup(pos + 1 + offset, undefined, {
                ...child.attrs,
                align: null,
              });
            }
          });
          return true;
        }
        if (node.isTextblock) {
          const $pos = state.doc.resolve(pos);
          if ($pos.parent.type !== nodes.list_item) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, align });
          }
          return false;
        }
        return true;
      });
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

// ─── lists (spec §4) ────────────────────────────────────────────────

const isListType = (type: NodeType) =>
  type === nodes.bullet_list || type === nodes.ordered_list;

/** Innermost list containing the whole selection, with its position. */
export const enclosingList = (
  state: EditorState,
): { node: PmNode; pos: number; depth: number } | null => {
  const { $from, $to } = state.selection;
  const shared = $from.sharedDepth($to.pos);
  for (let d = shared; d > 0; d--) {
    const node = $from.node(d);
    if (isListType(node.type)) return { node, pos: $from.before(d), depth: d };
  }
  return null;
};

export const isInList = (state: EditorState): boolean =>
  enclosingList(state) !== null;

export type ListChoice = "none" | "bullet" | "number" | "checkmark" | "icons";

const ICON_LIST_DEFAULT = { icon: "ri-check-fill", unicode: "eb7a" };

const glyphFor = (unicode: string) => {
  const code = Number.parseInt(unicode, 16);
  return Number.isNaN(code) ? "" : String.fromCharCode(code);
};

const listSpecFor = (
  choice: Exclude<ListChoice, "none">,
  icon: { icon: string; unicode: string },
): {
  type: NodeType;
  attrs: Record<string, unknown>;
  item: Record<string, unknown>;
} => {
  if (choice === "number")
    return {
      type: nodes.ordered_list,
      attrs: {},
      item: { icon: null, iconName: null },
    };
  const kind: ListKind = choice === "bullet" ? "bullet" : choice;
  return {
    type: nodes.bullet_list,
    attrs: { kind, iconName: choice === "icons" ? icon.icon : null },
    item:
      choice === "icons"
        ? { icon: glyphFor(icon.unicode), iconName: icon.icon }
        : { icon: null, iconName: null },
  };
};

type LiftedItem = {
  /** A position inside the item's first paragraph. */
  inside: number;
  style: TextStyleId | null;
  attrs: BlockAttrs;
};

/** The items a lift over `from`–`to` may take out of their list. */
const itemsBetween = (doc: PmNode, from: number, to: number): LiftedItem[] => {
  const items: LiftedItem[] = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (node.type === nodes.list_item) {
      items.push({
        inside: pos + 2,
        style: (node.attrs.textStyle as TextStyleId | null) ?? null,
        attrs: blockAttrsOf(node),
      });
    }
    return true;
  });
  return items;
};

/**
 * An item lifted out of every list is left a bare paragraph — the <li> held
 * its alignment, direction and line height, so the line jumped to the
 * layout's alignment (QA D6). Give back what the item carried, and a styled
 * item its heading (the inverse of wrapInListKeepingStyles, so Title → list →
 * no list is still a Title). Items still inside a list keep their own attrs.
 */
const restoreLiftedItems = (
  tr: Transaction,
  items: LiftedItem[],
  mapping: Mappable,
) => {
  for (const { inside, style, attrs } of items) {
    const $pos = tr.doc.resolve(mapping.map(inside));
    if ($pos.depth !== 1 || $pos.parent.type !== nodes.paragraph) continue;
    const own = blockAttrsOf($pos.parent);
    const merged = {
      align: own.align ?? attrs.align,
      lineHeight: own.lineHeight ?? attrs.lineHeight,
      direction: own.direction ?? attrs.direction,
      animation: own.animation ?? attrs.animation,
    };
    const level = style ? styleToHeadingLevel(style) : null;
    tr.setNodeMarkup(
      $pos.before(),
      level ? nodes.heading : undefined,
      level ? { level, ...merged } : { ...$pos.parent.attrs, ...merged },
    );
  }
};

/** liftListItem that keeps what each item carried (see restoreLiftedItems). */
const liftItemKeepingAttrs: Command = (state, dispatch) => {
  const { from, to } = state.selection;
  const items = itemsBetween(state.doc, from, to);
  return liftListItem(nodes.list_item)(
    state,
    dispatch &&
      ((tr) => {
        restoreLiftedItems(tr, items, tr.mapping);
        dispatch(tr);
      }),
  );
};

/** Remove list formatting around the selection entirely (all levels). */
const liftOutOfLists = (view: EditorView) => {
  const { anchor, head } = view.state.selection;
  // Froala parity: a caret removes the whole list, a range only its items.
  // The whole list is the outermost one — from a nested item the innermost
  // alone came off and the rest stayed a list.
  if (view.state.selection.empty) {
    const { $from } = view.state.selection;
    let depth = 1;
    while (depth <= $from.depth && !isListType($from.node(depth).type)) depth++;
    if (depth > $from.depth) return;
    const { doc } = view.state;
    view.dispatch(
      view.state.tr.setSelection(
        TextSelection.between(
          doc.resolve($from.before(depth) + 1),
          doc.resolve($from.after(depth) - 1),
        ),
      ),
    );
  }
  const { from, to } = view.state.selection;
  const items = itemsBetween(view.state.doc, from, to);
  const mapping = new Mapping();
  const dispatch = (tr: Transaction) => {
    mapping.appendMapping(tr.mapping);
    view.dispatch(tr);
  };
  // Lift level by level until no list is left in the range. Each pass lifts
  // the selected items of the first list there; a nested list it exposes is
  // lifted on a later pass (stopping once the selection itself left the list
  // kept those nested lists).
  for (let i = 0; i < 4 * MAX_LIST_DEPTH; i++) {
    const start = mapping.map(from, -1);
    const end = mapping.map(to, 1);
    const { doc } = view.state;
    const lists: Array<{ node: PmNode; pos: number }> = [];
    doc.nodesBetween(start, end, (node, pos) => {
      if (lists.length) return false;
      if (isListType(node.type)) lists.push({ node, pos });
      return !lists.length;
    });
    if (!lists.length) break;
    const { node, pos } = lists[0];
    view.dispatch(
      view.state.tr.setSelection(
        TextSelection.between(
          doc.resolve(Math.max(pos + 1, start)),
          doc.resolve(Math.min(pos + node.nodeSize - 1, end)),
        ),
      ),
    );
    if (!liftListItem(nodes.list_item)(view.state, dispatch)) break;
  }
  const tr = view.state.tr;
  restoreLiftedItems(tr, items, mapping);
  // The passes above select whole lists; give the user back their own caret
  // or range, or the next list click lists only the lifted items (QA D6).
  tr.setSelection(
    TextSelection.between(
      tr.doc.resolve(mapping.map(anchor)),
      tr.doc.resolve(mapping.map(head)),
    ),
  );
  view.dispatch(tr);
};

/**
 * Items hold paragraphs (schema), so `wrapInList` alone refuses a heading. The
 * heading becomes a paragraph and its style moves onto the item (spec §3.7) —
 * Froala likewise carried the heading typography onto the <li>.
 */
const wrapInListKeepingStyles = (
  view: EditorView,
  type: NodeType,
  attrs: Record<string, unknown>,
): boolean => {
  const { state } = view;
  const { from, to } = state.selection;
  const tr = state.tr;
  const styled: Array<{ inside: number; style: TextStyleId }> = [];
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type !== nodes.heading) return !node.isTextblock;
    styled.push({
      inside: pos + 1,
      style: headingLevelToStyle(node.attrs.level as number),
    });
    tr.setNodeMarkup(pos, nodes.paragraph, blockAttrsOf(node));
    return false;
  });

  let wrap: Transaction | null = null;
  const wrapped = wrapInList(type, attrs)(state.apply(tr), (t) => {
    wrap = t;
  });
  if (!wrapped || !wrap) return false;
  const wrapTr: Transaction = wrap;
  for (const step of wrapTr.steps) tr.step(step);

  for (const { inside, style } of styled) {
    const $pos = tr.doc.resolve(wrapTr.mapping.map(inside));
    const item = $pos.node(-1);
    if (item.type !== nodes.list_item) continue;
    tr.setNodeMarkup($pos.before(-1), undefined, {
      ...item.attrs,
      textStyle: style,
    });
  }
  view.dispatch(tr.scrollIntoView());
  return true;
};

type ListSpec = ReturnType<typeof listSpecFor>;

/** A list (and every list nested in it) re-typed to `spec`. */
const retypeList = (list: PmNode, spec: ListSpec): PmNode => {
  const items: PmNode[] = [];
  list.forEach((item) => {
    const children: PmNode[] = [];
    item.forEach((child) =>
      children.push(isListType(child.type) ? retypeList(child, spec) : child),
    );
    items.push(
      nodes.list_item.create({ ...item.attrs, ...spec.item }, children),
    );
  });
  return spec.type.create(spec.attrs, items);
};

/** A top-level text block as a list item; a heading keeps its style on the item. */
const blockToItem = (block: PmNode, spec: ListSpec): PmNode => {
  const isHeading = block.type === nodes.heading;
  const { align, ...rest } = blockAttrsOf(block);
  return nodes.list_item.create(
    {
      ...spec.item,
      // The marker follows the item's alignment, so it lives on the item.
      align,
      textStyle: isHeading
        ? headingLevelToStyle(block.attrs.level as number)
        : null,
    },
    nodes.paragraph.create(
      { ...rest, align: null },
      block.content,
      block.marks,
    ),
  );
};

/** Content start of every text block, in document order. */
const textblockStarts = (doc: PmNode): number[] => {
  const starts: number[] = [];
  doc.descendants((node, pos) => {
    if (node.isTextblock) starts.push(pos + 1);
    return !node.isTextblock;
  });
  return starts;
};

/**
 * A caret applies a list type to the whole element, like Froala (it selected
 * all first, QA D6): every list at every level switches type, and paragraphs
 * outside lists join as items — one list, in document order.
 */
const listWholeElement = (state: EditorState, spec: ListSpec): Transaction => {
  const items: PmNode[] = [];
  state.doc.forEach((block) => {
    if (isListType(block.type)) {
      retypeList(block, spec).forEach((item) => items.push(item));
    } else if (block.isTextblock) {
      items.push(blockToItem(block, spec));
    }
  });
  return state.tr.replaceWith(
    0,
    state.doc.content.size,
    spec.type.create(spec.attrs, items),
  );
};

/**
 * Toolbar list control. Switching type re-tags the enclosing list in place;
 * checkmark/icon lists are bullet lists with a marker kind (Storydoc shape).
 */
export const setList = (
  view: EditorView,
  choice: ListChoice,
  icon: { icon: string; unicode: string } = ICON_LIST_DEFAULT,
): boolean => {
  if (choice === "none") {
    liftOutOfLists(view);
    return true;
  }
  const spec = listSpecFor(choice, icon);
  if (view.state.selection.empty) {
    const { state } = view;
    const tr = listWholeElement(state, spec);
    // The rewrite keeps every text block in order: put the caret back in its
    // block, not at the end of the element (QA D6).
    const { $from } = state.selection;
    const start = textblockStarts(tr.doc)[
      textblockStarts(state.doc).indexOf($from.start())
    ];
    if (start != null)
      tr.setSelection(TextSelection.create(tr.doc, start + $from.parentOffset));
    view.dispatch(tr.scrollIntoView());
    return true;
  }
  const current = enclosingList(view.state);

  if (!current) {
    if (!wrapInListKeepingStyles(view, spec.type, spec.attrs)) return false;
  }
  const list = enclosingList(view.state);
  if (!list) return false;

  const tr = view.state.tr;
  tr.setNodeMarkup(list.pos, spec.type, spec.attrs);
  view.state.doc.nodesBetween(
    list.pos + 1,
    list.pos + list.node.nodeSize - 1,
    (node, pos) => {
      if (node.type === nodes.list_item) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...spec.item });
      }
      return true;
    },
  );
  view.dispatch(tr.scrollIntoView());
  return true;
};

/** Tab — indent, but never past MAX_LIST_DEPTH (whole selection, subtree kept). */
export const indentListItem: Command = (state, dispatch) => {
  const { $from, $to } = state.selection;
  const depth = listDepthAt(state.doc, $from.pos);
  if (depth === 0) return false;
  // The items Tab moves: siblings in the innermost list around the selection.
  // Only THEIR nested lists count — walking the whole range also visited the
  // ancestor items and counted the parents' nesting, so level 4 was never
  // reachable (QA D2).
  const range = $from.blockRange(
    $to,
    (node) => node.childCount > 0 && node.firstChild?.type === nodes.list_item,
  );
  if (!range) return false;
  let subtree = 0;
  for (let i = range.startIndex; i < range.endIndex; i++) {
    subtree = Math.max(subtree, maxListDepthIn(range.parent.child(i).content));
  }
  if (depth + 1 + subtree > MAX_LIST_DEPTH) return false;
  return sinkListItem(nodes.list_item)(state, dispatch);
};

export const outdentListItem: Command = liftItemKeepingAttrs;

/** Enter — new item, or exit the list from an empty item (spec §4.5). */
export const splitOrExitListItem: Command = (state, dispatch) => {
  const { $from } = state.selection;
  if ($from.depth < 2 || $from.node(-1).type !== nodes.list_item) return false;
  if ($from.parent.content.size === 0) {
    return liftItemKeepingAttrs(state, dispatch);
  }
  return splitListItem(nodes.list_item)(state, dispatch);
};

/** Backspace in an empty item — outdent (root level becomes a paragraph). */
export const backspaceInEmptyListItem: Command = (state, dispatch) => {
  const { $from, empty } = state.selection;
  if (!empty || $from.depth < 2 || $from.node(-1).type !== nodes.list_item)
    return false;
  if ($from.parent.content.size !== 0 || $from.index(-1) !== 0) return false;
  const item = $from.node(-1);
  const isFirstTopLevelItem =
    $from.index(-2) === 0 && listDepthAt(state.doc, $from.pos) === 1;
  // An item with sub-items keeps them: the start-of-item join moves them up.
  if (item.childCount > 1) return false;
  const above = Selection.findFrom(
    state.doc.resolve($from.before(-1)),
    -1,
    true,
  );
  // The first item of a top-level list has no line of its own above it, so
  // it still becomes a paragraph.
  if (isFirstTopLevelItem || !above)
    return liftItemKeepingAttrs(state, dispatch);
  // Any other empty item goes, and the caret returns to the end of the line
  // above. Lifting it moved it out one level per press, so leaving an empty
  // fourth-level item took four Backspaces (product QA D3).
  if (dispatch) {
    const list = $from.node(-2);
    const from = list.childCount === 1 ? $from.before(-2) : $from.before(-1);
    const to = list.childCount === 1 ? $from.after(-2) : $from.after(-1);
    const tr = state.tr.delete(from, to);
    tr.setSelection(TextSelection.create(tr.doc, above.$head.pos));
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/**
 * Backspace at the start of an item whose only other content is sub-lists:
 * its text joins the line above and its sub-items take the item's place in
 * its list. joinTextblockBackward moved just the text and left an empty
 * bullet holding the sub-items (QA D3). Anything else in the item (a second
 * paragraph) goes the default way, which keeps it.
 */
const joinItemWithSubItemsBackward: Command = (state, dispatch) => {
  const { $from } = state.selection;
  const item = $from.node(-1);
  const text = item.firstChild;
  if (!text || item.childCount < 2) return false;
  const subItems: PmNode[] = [];
  let onlyLists = true;
  item.content.cut(text.nodeSize).forEach((child) => {
    if (isListType(child.type)) child.forEach((li) => subItems.push(li));
    else onlyLists = false;
  });
  if (!onlyLists) return false;
  const itemPos = $from.before(-1);
  const above = Selection.findFrom(state.doc.resolve(itemPos), -1, true);
  if (!above) return false;
  if (dispatch) {
    const joinAt = above.$head.pos;
    const tr = state.tr
      .replaceWith(itemPos, itemPos + item.nodeSize, subItems)
      .insert(joinAt, text.content);
    tr.setSelection(TextSelection.create(tr.doc, joinAt));
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/**
 * Backspace at the start of a non-empty item merges it into the line above —
 * the previous item, or the parent item's text for the first item of a nested
 * list (QA D3). The first item of a top-level list has nothing above it in the
 * list, so it becomes a paragraph instead. Without this the key fell through
 * to selectNodeBackward, which selected the line above and the next Backspace
 * erased it.
 */
export const backspaceAtListItemStart: Command = (state, dispatch, view) => {
  const { $from, empty } = state.selection;
  if (!empty || $from.parentOffset !== 0 || $from.depth < 3) return false;
  if ($from.node(-1).type !== nodes.list_item || $from.index(-1) !== 0)
    return false;
  const isFirstItem = $from.index(-2) === 0;
  const isTopLevel = listDepthAt(state.doc, $from.pos) === 1;
  if (isFirstItem && isTopLevel) {
    return liftItemKeepingAttrs(state, dispatch);
  }
  return (
    joinItemWithSubItemsBackward(state, dispatch) ||
    joinTextblockBackward(state, dispatch, view)
  );
};

// ─── links (spec §8) ────────────────────────────────────────────────

export interface LinkInput {
  url: string;
  type: string; // "hyperlink" | "anchor"
  event?: string;
  openInPopup?: boolean;
}

const ANCHOR_HREF = "javascript:void(0);";

export const normalizeUrl = (url: string): string => {
  const trimmed = url.trim();
  if (/^(https?:|mailto:|tel:|#)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const linkAttrsFor = (input: LinkInput) =>
  input.type === "anchor"
    ? {
        href: ANCHOR_HREF,
        target: null,
        anchor: input.url.replace(/^#/, ""),
        openInPopup: null,
        event: null,
      }
    : {
        href: normalizeUrl(input.url),
        target: "_blank",
        openInPopup: !!input.openInPopup,
        event: input.event || null,
        anchor: null,
      };

/** Range a link command acts on: selection, or the link the caret is in. */
const linkRange = (state: EditorState): { from: number; to: number } | null => {
  const { $from, from, to, empty } = state.selection;
  if (!empty) return { from, to };
  const extent = markExtentAt($from, marks.link);
  return extent ? { from: extent.from, to: extent.to } : null;
};

export const applyLink =
  (input: LinkInput): Command =>
  (state, dispatch) => {
    const range = linkRange(state);
    if (!range || !input.url) return false;
    if (dispatch) {
      dispatch(
        state.tr
          .removeMark(range.from, range.to, marks.link)
          .addMark(range.from, range.to, marks.link.create(linkAttrsFor(input)))
          .scrollIntoView(),
      );
    }
    return true;
  };

export const removeLink: Command = (state, dispatch) => {
  const range = linkRange(state);
  if (!range) return false;
  if (dispatch) dispatch(state.tr.removeMark(range.from, range.to, marks.link));
  return true;
};

/** Paste-a-URL-over-text (spec §8): selected text becomes a link. */
export const linkSelectionTo =
  (url: string): Command =>
  (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (empty) return false;
    if (dispatch) {
      dispatch(
        state.tr
          .removeMark(from, to, marks.link)
          .addMark(
            from,
            to,
            marks.link.create({ href: normalizeUrl(url), target: "_blank" }),
          ),
      );
    }
    return true;
  };

// ─── inline atoms (spec §5) ─────────────────────────────────────────

export const insertIcon =
  (data: { unicode: string; icon: string }): Command =>
  (state, dispatch) => {
    const family = data.icon.startsWith("fa-") ? "fa" : "remix";
    if (dispatch) {
      const node = nodes.icon.create({ family, unicode: data.unicode });
      dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    }
    return true;
  };

export const insertEmoji =
  (shortcodes: string): Command =>
  (state, dispatch) => {
    if (dispatch) {
      const node = nodes.emoji.create({ shortcodes });
      dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    }
    return true;
  };

// ─── remaining toolbar controls (parity with the Froala toolbar) ────

/**
 * Font size control: raw pixel sizes are never stored (spec §3.8), so a
 * picked size is mapped onto the five styles through the §3.5 table and
 * applied as a text style.
 */
export const setFontSize =
  (px: number): Command =>
  (state, dispatch) => {
    // Like colour: the size goes on the text (caret → whole element), the
    // block keeps its tag — an H1 at 12px is still an H1.
    const { from, to } = targetRange(state);
    if (dispatch) {
      const tr = state.tr.removeMark(from, to, marks.font_size);
      // DEFAULT_FONT_SIZE (0) only removes: the text falls back to the design
      // system size its block class resolves to.
      if (px > DEFAULT_FONT_SIZE)
        tr.addMark(from, to, marks.font_size.create({ px }));
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

/** Blocks touched by the selection (a caret → its own block), with positions. */
const selectedBlocks = (
  state: EditorState,
  { from, to }: { from: number; to: number } = state.selection,
): Array<{ node: PmNode; pos: number }> => {
  const blocks: Array<{ node: PmNode; pos: number }> = [];
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type === nodes.list_item) {
      blocks.push({ node, pos });
      return true;
    }
    if (node.isTextblock) {
      const $pos = state.doc.resolve(pos);
      if ($pos.parent.type !== nodes.list_item) blocks.push({ node, pos });
      return false;
    }
    return true;
  });
  return blocks;
};

const setBlockAttrs =
  (patch: Partial<BlockAttrs>, { wholeOnCaret = false } = {}): Command =>
  (state, dispatch) => {
    const blocks = selectedBlocks(
      state,
      wholeOnCaret ? targetRange(state) : state.selection,
    );
    if (blocks.length === 0) return false;
    if (dispatch) {
      const tr = state.tr;
      for (const { node, pos } of blocks) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch });
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

/** Line height ("1", "1.15", "1.5", "2") on the selected blocks. */
export const setLineHeight = (value: string): Command =>
  setBlockAttrs({ lineHeight: value });

/**
 * Text direction; like Froala, RTL also right-aligns and LTR left-aligns. A
 * caret applies it to the whole element (Froala selected all first, QA D8).
 */
export const setTextDirection = (direction: "ltr" | "rtl"): Command =>
  setBlockAttrs(
    {
      direction: (direction === "rtl" ? "rtl" : null) as Direction,
      align: direction === "rtl" ? "right" : "left",
    },
    { wholeOnCaret: true },
  );

/** Text animation on the selected blocks (null removes it). */
export const setTextAnimation = (data: RichTextTextAnimation | null): Command =>
  setBlockAttrs({
    animation: data
      ? { speed: data.speed, baseOpacity: data.baseOpacity }
      : null,
  });

/** KPI counter nodes inside the selection (or the selected node itself). */
const rkpisInSelection = (
  state: EditorState,
): Array<{ node: PmNode; pos: number }> => {
  const { selection } = state;
  if (
    selection instanceof NodeSelection &&
    selection.node.type === nodes.rkpi
  ) {
    return [{ node: selection.node, pos: selection.from }];
  }
  const found: Array<{ node: PmNode; pos: number }> = [];
  state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    if (node.type === nodes.rkpi) found.push({ node, pos });
    return true;
  });
  return found;
};

export const selectedRkpi = (state: EditorState): RkpiAttrs | null =>
  (rkpisInSelection(state)[0]?.node.attrs as RkpiAttrs | undefined) ?? null;

/** Create a counter from the selection, or update the counter(s) selected. */
export const saveRkpi =
  (data: RichTextRkpiSave): Command =>
  (state, dispatch) => {
    const attrs: RkpiAttrs = {
      to: data.to,
      from: 0,
      prefix: data.prefix ?? "",
      suffix: data.suffix ?? "",
      speed: data.speed ?? "medium",
      decimal: !!data.decimal,
    };
    const existing = rkpisInSelection(state);
    if (dispatch) {
      const tr = state.tr;
      if (existing.length) {
        for (const { pos } of existing) tr.setNodeMarkup(pos, undefined, attrs);
      } else {
        tr.replaceSelectionWith(nodes.rkpi.create(attrs), false);
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

/**
 * Removing a counter keeps its number as plain text (with its formatting) and
 * drops only the animation — the reader saw a number there (QA F8).
 */
export const deleteRkpi: Command = (state, dispatch) => {
  const existing = rkpisInSelection(state);
  if (existing.length === 0) return false;
  if (dispatch) {
    const tr = state.tr;
    // From the end so earlier positions stay valid.
    for (const { node, pos } of [...existing].reverse()) {
      const text = rkpiText(node.attrs as RkpiAttrs);
      tr.replaceWith(
        pos,
        pos + node.nodeSize,
        textSchema.text(text, node.marks),
      );
    }
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/** Admin AI classification: tag the selection (or the caret's block); null clears. */
export const setAiClassification =
  (value: string | null): Command =>
  (state, dispatch) => {
    const { $from, from, to, empty } = state.selection;
    const range = empty
      ? { from: $from.start(), to: $from.end() }
      : { from, to };
    if (dispatch) {
      const tr = state.tr.removeMark(
        range.from,
        range.to,
        marks.ai_classification,
      );
      if (value) {
        tr.addMark(
          range.from,
          range.to,
          marks.ai_classification.create({ value }),
        );
      }
      dispatch(tr);
    }
    return true;
  };

/** AI "insert text": replaces the selection; a caret gets a trailing space. */
export const insertAiText =
  (text: string): Command =>
  (state, dispatch) => {
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (dispatch) {
      const { empty } = state.selection;
      dispatch(
        state.tr.insertText(empty ? `${trimmed} ` : trimmed).scrollIntoView(),
      );
    }
    return true;
  };

/**
 * Toolbar variant of setMarkFromFirstSegment (Froala parity): with a caret
 * the whole element is the target — the on/off decision comes from the
 * caret's marks and is applied to all text. With a selection it behaves like
 * setMarkFromFirstSegment. Keyboard shortcuts keep the stored-mark behaviour
 * so typing continues in the new state.
 */
export const setMarkOnElementOrSelection =
  (type: MarkType): Command =>
  (state, dispatch) => {
    if (!state.selection.empty) {
      return setMarkFromFirstSegment(type)(state, dispatch);
    }
    const { from, to } = targetRange(state);
    const active = !!type.isInSet(firstSegmentMarks(state));
    if (dispatch) {
      const tr = state.tr.removeMark(from, to, type);
      if (!active) tr.addMark(from, to, type.create());
      dispatch(tr.scrollIntoView());
    }
    return true;
  };

/**
 * Enter outside lists. Like Froala's DOM split, the new block keeps the old
 * block's attributes (alignment, line height, direction, animation) so both
 * the editor and the read-only render show it the same way; at the end of a
 * heading the new block is a paragraph (ProseMirror default). Stored marks
 * carry over so typing continues bold/italic (splitBlockKeepMarks parity).
 */
export const splitBlockKeepAttrs: Command = (state, dispatch) =>
  splitBlockAs((node, atEnd) => {
    if (!node.isTextblock) return null;
    const attrs = blockAttrsOf(node);
    if (node.type === nodes.heading && !atEnd) {
      return {
        type: nodes.heading,
        attrs: { ...attrs, level: node.attrs.level },
      };
    }
    return { type: nodes.paragraph, attrs };
  })(
    state,
    dispatch &&
      ((tr) => {
        const marks =
          state.storedMarks ||
          (state.selection.$to.parentOffset && state.selection.$from.marks());
        if (marks) tr.ensureMarks(marks);
        // At a heading's start splitBlockAs turns the empty line above into a
        // bare paragraph; it keeps the heading's alignment and spacing (QA C10).
        const { $from } = state.selection;
        if ($from.parentOffset === 0 && $from.parent.type === nodes.heading) {
          const above = tr.mapping.map($from.before(), -1);
          if (tr.doc.nodeAt(above)?.type === nodes.paragraph)
            tr.setNodeMarkup(above, null, blockAttrsOf($from.parent));
        }
        dispatch(tr);
      }),
  );
